import type { BibleBook } from "./types";

/** Joshua to Esther — the narrative spine of the Old Testament. */
export const HISTORY: BibleBook[] = [
  {
    slug: "joshua",
    name: "Joshua",
    testament: "old",
    genre: "history",
    order: 6,
    chapters: 24,
    author: "Anonymous; part of the Deuteronomistic history",
    written: "Final form likely sixth century BC",
    audience: "Israel, arguing about how it came to be where it lives",
    argument:
      "The conquest account, and the book that raises the hardest moral question in the Old Testament without softening it.",
    summary: [
      "Joshua covers the entry into the land: the river crossing, Jericho, a string of campaigns, and then a long land-allocation section that reads like a deed register because that is what it is.",
      "It is also the book modern readers find most difficult, and honestly so. The conquest narratives describe the destruction of populations under divine instruction, and no responsible reading can skip that. Christians have answered it in several ways — that the language is the standard hyperbole of ancient war reporting, that the judgement is on specific practices named earlier, that the archaeology suggests a slower and messier settlement than the text's summary. None of those answers is comfortable and the text does not offer one.",
      "The final chapter turns it into a decision: a covenant renewal at Shechem, and a choice put to the people in front of witnesses.",
    ],
    startHere:
      "Chapter 1 for the commission, then jump to chapter 24 for the covenant renewal. The allocation chapters in between are reference material rather than reading.",
    themes: ["land", "conquest", "covenant", "allocation"],
    keyChapters: [
      {
        chapter: 1,
        why: "The commission — be strong and courageous, said to a man taking over from Moses.",
      },
      {
        chapter: 6,
        why: "Jericho, the campaign everybody knows and the one that raises the book's hardest question.",
      },
      {
        chapter: 24,
        why: "Covenant renewal at Shechem, where the choice is put to the people in front of witnesses.",
      },
    ],
    known: {
      text: "Haven't I commanded you? Be strong and courageous. Don't be afraid. Don't be dismayed, for Yahweh your God is with you wherever you go.",
      ref: "Joshua 1:9",
    },
    misread:
      "That Joshua 1:9 is a general encouragement. It is a military commission given to one man taking over from Moses with a campaign in front of him, and the courage asked for is specifically the courage to do the thing he has been told to do.",
    faq: [
      {
        q: "What is the book of Joshua about?",
        a: "Israel entering and settling the land after the wilderness years — the crossing, the campaigns, the allocation of territory, and a closing covenant renewal.",
      },
      {
        q: "How should Christians read the conquest in Joshua?",
        a: "Carefully, and without pretending it is easy. The main responses point to ancient war rhetoric, to the specific practices named as the grounds of judgement, and to archaeological evidence of a slower settlement. None removes the difficulty.",
      },
    ],
    topics: ["bible-verses-about-strength"],
  },
  {
    slug: "judges",
    name: "Judges",
    testament: "old",
    genre: "history",
    order: 7,
    chapters: 21,
    author: "Anonymous; part of the Deuteronomistic history",
    written: "Final form likely sixth century BC",
    audience: "Readers being shown what disorder costs",
    argument:
      "A spiral, not a sequence: six cycles of collapse and rescue in which each deliverer is worse than the last and the book ends in atrocity.",
    summary: [
      "Judges has a repeating structure that is easy to spot and easy to underestimate. The people abandon the covenant, are oppressed, cry out, are rescued by a judge, and have peace until the judge dies. Then it happens again.",
      "What makes the book more than a loop is that the quality declines each time. Deborah is admirable. Gideon needs three signs and makes an idol at the end. Jephthah sacrifices his daughter. Samson is a disaster with strength attached. By the closing chapters the narrative has stopped commenting at all and simply reports a sequence of horrors, twice pausing to observe that there was no king in Israel and everyone did what was right in their own eyes.",
      "It is the bleakest book in the Old Testament and it is constructed that way deliberately.",
    ],
    startHere:
      "Chapter 4, the Deborah and Barak cycle, which shows the pattern at its cleanest. Then chapter 6 for Gideon, where the decline becomes visible.",
    themes: ["cycles", "decline", "deliverers", "disorder"],
    keyChapters: [
      {
        chapter: 4,
        why: "Deborah, the cycle at its cleanest and with its most capable leader.",
      },
      {
        chapter: 7,
        why: "Gideon's three hundred, and the signs he needed before he would go.",
      },
      {
        chapter: 19,
        why: "The chapter the lectionaries skip, and the one that explains the book's closing refrain.",
      },
    ],
    known: {
      text: "In those days there was no king in Israel. Everyone did that which was right in his own eyes.",
      ref: "Judges 21:25",
    },
    misread:
      "That the judges are heroes. The book is not holding them up as models; it is documenting a slide, and several of its central figures are presented as increasingly unfit for the role they occupy.",
    faq: [
      {
        q: "What is the cycle in the book of Judges?",
        a: "Abandonment, oppression, crying out, rescue by a judge, peace, and then repetition. It occurs six times, and the deliverers get worse with each iteration.",
      },
      {
        q: "Why does Judges end so badly?",
        a: "Because the ending is the argument. The last chapters describe atrocities without comment except for the refrain that there was no king and everyone did what was right in their own eyes — which sets up the books that follow.",
      },
    ],
  },
  {
    slug: "ruth",
    name: "Ruth",
    testament: "old",
    genre: "history",
    order: 8,
    chapters: 4,
    author: "Anonymous",
    written:
      "Uncertain; proposals range from the tenth to the fifth century BC",
    audience: "Readers of Judges, given a counter-example",
    argument:
      "Four chapters about a foreign widow and a field, placed immediately after the darkest book in the Bible, showing what ordinary loyalty looks like in the same period.",
    summary: [
      "Ruth is set in the time of the judges and reads nothing like Judges. There are no battles, no miracles and no national events; there is a famine, two deaths, a return journey, a harvest and a marriage.",
      "The whole book turns on a Moabite woman's decision to stay with her mother-in-law when every reason of self-interest says leave. Everything after that is the working out of that loyalty through the ordinary machinery of ancient law — gleaning rights, the redemption of family land, a legal scene at a town gate.",
      "The last four verses reveal why the book is in the canon: this foreign woman is David's great-grandmother, and Matthew's genealogy names her.",
    ],
    startHere:
      "Chapter 1, and read the whole thing. It is four chapters and takes about fifteen minutes, which makes it the best entry point into the Old Testament there is.",
    themes: ["loyalty", "gleaning", "redemption", "belonging"],
    keyChapters: [
      {
        chapter: 1,
        why: "The speech everybody quotes, said by a widow to her mother-in-law on a road.",
      },
      {
        chapter: 2,
        why: "Gleaning in Boaz's field, which is ancient welfare law working as intended.",
      },
      {
        chapter: 4,
        why: "The legal scene at the gate, and the genealogy that explains why the book is here.",
      },
    ],
    known: {
      text: "Where you go, I will go; and where you stay, I will stay.",
      ref: "Ruth 1:16",
    },
    misread:
      "That it is a love story. The famous speech is said by a widow to her mother-in-law, not to a husband, and the book's subject is covenant loyalty between two women with no legal claim on each other.",
    faq: [
      {
        q: "Why is the book of Ruth important?",
        a: "It shows ordinary faithfulness during the period Judges describes as chaos, and it establishes that David — and therefore Jesus in Matthew's genealogy — descends from a Moabite woman.",
      },
      {
        q: "How long does it take to read Ruth?",
        a: "About fifteen minutes. Four chapters, one continuous story, and the easiest complete book in the Old Testament to read in a sitting.",
      },
    ],
  },
  {
    slug: "1-samuel",
    name: "1 Samuel",
    testament: "old",
    genre: "history",
    order: 9,
    chapters: 31,
    author: "Anonymous; part of the Deuteronomistic history",
    written: "Final form likely sixth century BC",
    audience: "A people who had asked for a king and got one",
    argument:
      "The book where Israel demands a monarchy, is warned in detail what it will cost, insists, and receives exactly what it was promised.",
    summary: [
      "1 Samuel moves through three figures: Samuel, the last judge; Saul, the first king; and David, the one who replaces him. The transition from tribal confederation to monarchy is the book's real subject, and it is treated with striking ambivalence.",
      "Chapter 8 is the passage to know. The people ask for a king to be like the other nations, and Samuel's response is a list of what a king will actually do — conscript their sons, take their daughters, take a tenth of everything, and eventually they will cry out and not be answered. They ask anyway.",
      "Saul's reign then plays as tragedy rather than villainy: a man out of his depth, undone by insecurity, and the book gives him a genuinely affecting end. David arrives as a shepherd, is chosen on grounds explicitly opposed to appearance, and spends most of the rest of the book being hunted.",
    ],
    startHere:
      "Chapter 8, where the political argument of the whole book is stated. Chapter 16 if you want David's introduction and its famous line.",
    themes: ["monarchy", "anointing", "jealousy", "the heart"],
    keyChapters: [
      {
        chapter: 8,
        why: "The people ask for a king and are told in detail what a king will cost them.",
      },
      {
        chapter: 16,
        why: "David anointed, and the reason given is set explicitly against appearance and height, which is what the previous king had going for him.",
      },
      {
        chapter: 24,
        why: "David spares Saul in the cave, and the restraint costs him nothing except the kingdom he has already been promised and could simply have taken.",
      },
    ],
    known: {
      text: "For man looks at the outward appearance, but Yahweh looks at the heart.",
      ref: "1 Samuel 16:7",
    },
    misread:
      "That Saul is simply a bad king and David a good one. The narrative is more careful than that — Saul is disobedient and pitiable, and David's own failures are recorded at length in the sequel.",
    faq: [
      {
        q: "What is 1 Samuel about?",
        a: "Israel's shift from judges to monarchy: Samuel's ministry, Saul's rise and unravelling, and David's anointing and years as a fugitive.",
      },
      {
        q: "Why did God let Israel have a king?",
        a: "Chapter 8 presents it as a concession with a warning attached. Samuel lists what a monarchy will cost them in detail; they insist, and the rest of the history is largely that list coming true.",
      },
    ],
  },
  {
    slug: "2-samuel",
    name: "2 Samuel",
    testament: "old",
    genre: "history",
    order: 10,
    chapters: 24,
    author: "Anonymous; part of the Deuteronomistic history",
    written: "Final form likely sixth century BC",
    audience: "A people whose national story is built on David",
    argument:
      "David's reign told in two halves that the book refuses to reconcile: the rise, and then everything that follows from one evening on a roof.",
    summary: [
      "The first ten chapters are ascent. David unites the kingdom, takes Jerusalem, brings the ark in dancing, and receives the promise that his house will be established forever — a promise the rest of the Bible keeps returning to.",
      "Chapter 11 changes the book. David sees Bathsheba, sends for her, and has her husband killed to cover it. What follows is not a moral appended to the story; it is the story. The rest of 2 Samuel is a family and a kingdom coming apart — a rape, a murder, a rebellion led by his own son, and David fleeing the city he took.",
      "The book's remarkable feature is that it is Israel's national epic and it includes all of this. No other ancient near-eastern royal document treats its founding king this way.",
    ],
    startHere:
      "Chapter 7 for the promise, then chapter 11, which is where the second half of the book begins.",
    themes: [
      "kingship",
      "promise",
      "adultery and consequence",
      "a house divided",
    ],
    keyChapters: [
      {
        chapter: 7,
        why: "The promise of an everlasting house, which the rest of the Bible keeps returning to.",
      },
      {
        chapter: 11,
        why: "Bathsheba and Uriah, the chapter the second half of the book is a consequence of.",
      },
      {
        chapter: 18,
        why: "Absalom's death and David's grief, the most affecting scene in the Old Testament.",
      },
    ],
    known: {
      text: "Your house and your kingdom will be made sure forever before you. Your throne will be established forever.",
      ref: "2 Samuel 7:16",
    },
    misread:
      "That David's sin is an episode. The narrative treats it as the hinge of his reign, and every disaster in the remaining thirteen chapters is presented as consequence rather than coincidence.",
    faq: [
      {
        q: "What happens in 2 Samuel?",
        a: "David becomes king over all Israel, takes Jerusalem, receives the promise of an everlasting house — and then commits adultery and murder, after which the book documents his family and kingdom fracturing.",
      },
      {
        q: "Why is David called a man after God's own heart?",
        a: "The phrase is from 1 Samuel 13 and describes his selection rather than his conduct. 2 Samuel is unsparing about the conduct, which is part of why the description has been argued about ever since.",
      },
    ],
  },
  {
    slug: "1-kings",
    name: "1 Kings",
    testament: "old",
    genre: "history",
    order: 11,
    chapters: 22,
    author: "Anonymous; part of the Deuteronomistic history",
    written: "Final form likely sixth century BC",
    audience: "Exiles asking how it came to this",
    argument:
      "Solomon's temple and Solomon's compromise, then the kingdom splitting in two and a prophet on a mountain discovering God is not in the earthquake.",
    summary: [
      "The first eleven chapters are Solomon: the wisdom, the temple, the wealth, and then the seven hundred wives and the shrines built for their gods. The book presents the decline as gradual and entirely self-inflicted.",
      "Chapter 12 splits the kingdom. Ten tribes go north under Jeroboam, two remain south under Rehoboam, and from here the narrative alternates between two royal lines, evaluating each king by one criterion that has nothing to do with military or economic success.",
      "The second half belongs to Elijah, whose contest on Carmel is the most theatrical scene in the Old Testament and whose collapse immediately afterwards is the most human. The still small voice at Horeb is the answer he gets, and it is deliberately not the answer Carmel would have predicted.",
    ],
    startHere:
      "Chapter 18 for Carmel and chapter 19 for what happens to Elijah the next day. Read them together; separating them is what turns the story into a triumph.",
    themes: ["temple", "wisdom and compromise", "division", "prophets"],
    keyChapters: [
      {
        chapter: 8,
        why: "The temple dedication and Solomon's prayer, the high point of the united kingdom.",
      },
      {
        chapter: 12,
        why: "The split, caused by a young king refusing to lighten a labour burden.",
      },
      {
        chapter: 19,
        why: "Elijah at Horeb, exhausted and suicidal, and the still small voice.",
      },
    ],
    known: {
      text: "After the earthquake a fire passed; but Yahweh was not in the fire. After the fire, there was a still small voice.",
      ref: "1 Kings 19:12",
    },
    misread:
      "That Solomon is a straightforward success. The book gives his failures as much space as his temple and traces the kingdom's division directly to them.",
    faq: [
      {
        q: "Why did the kingdom split in 1 Kings?",
        a: "Rehoboam refused to lighten the labour burden Solomon had imposed, and ten tribes seceded. The narrative presents the split as the consequence of Solomon's reign rather than his son's tactlessness alone.",
      },
      {
        q: "What is the still small voice?",
        a: "Elijah, exhausted and suicidal after his victory at Carmel, is met at Horeb — not in wind, earthquake or fire, but in a low whisper. It is the book's correction to the assumption that God works mainly through spectacle.",
      },
    ],
  },
  {
    slug: "2-kings",
    name: "2 Kings",
    testament: "old",
    genre: "history",
    order: 12,
    chapters: 25,
    author: "Anonymous; part of the Deuteronomistic history",
    written: "Final form likely sixth century BC",
    audience: "People in Babylon, reading how they got there",
    argument:
      "The book that ends with both kingdoms destroyed, written by people living in the wreckage and refusing to blame anyone but themselves.",
    summary: [
      "2 Kings continues the alternating royal chronicle and carries it to its end: the northern kingdom falls to Assyria in 722 BC, the southern kingdom to Babylon in 586, and the last chapter has the temple burned and the people deported.",
      "In between are Elisha's ministry, a handful of reforming kings — Hezekiah and Josiah are the two the book admires — and a long accumulation of kings evaluated in a single sentence each. The pace is deliberate: it feels like a countdown because it is one.",
      "What is striking is the explanation offered. A defeated nation in the ancient world normally concluded its god was weaker. This history concludes the opposite, that the covenant was broken by the people and the outcome was announced in advance.",
    ],
    startHere:
      "Chapter 22, the discovery of the law book under Josiah, which is the last serious attempt to turn it around and the closest the history comes to naming its own thesis.",
    themes: ["decline", "reform", "exile", "accountability"],
    keyChapters: [
      {
        chapter: 6,
        why: "Elisha's servant shown the hills full of horses and chariots of fire.",
      },
      {
        chapter: 22,
        why: "The law book found during repairs, and Josiah tearing his clothes when it is read to him.",
      },
      {
        chapter: 25,
        why: "The temple burned and the people deported — the end the whole history has been counting down to.",
      },
    ],
    known: {
      text: "Don't be afraid, for those who are with us are more than those who are with them.",
      ref: "2 Kings 6:16",
    },
    misread:
      "That it is a list of kings. It is an argument about why the exile happened, structured as a chronicle, and it is the reason the prophets who saw it coming were preserved.",
    faq: [
      {
        q: "How does 2 Kings end?",
        a: "With Jerusalem destroyed, the temple burned and the population deported to Babylon — followed by a short note that the exiled king was later released from prison, which is the only glimmer in the closing verses.",
      },
      {
        q: "What is the message of 1 and 2 Kings?",
        a: "That the exile was the consequence of a broken covenant rather than of a weak God. Every king is measured against that standard and almost all of them fail it.",
      },
    ],
  },
  {
    slug: "1-chronicles",
    name: "1 Chronicles",
    testament: "old",
    genre: "history",
    order: 13,
    chapters: 29,
    author: "Anonymous; traditionally associated with Ezra",
    written: "Likely fourth century BC, after the return from exile",
    audience:
      "A small province rebuilding, asking whether it is still the same people",
    argument:
      "The same history retold for people who have come back from exile, and told with the failures left out on purpose.",
    summary: [
      "Nine chapters of genealogy open the book, and they are not filler. Written for a community that had just lost its continuity, a list establishing who descends from whom is the argument: you are still the same people.",
      "The narrative that follows retells David's reign, and the differences from 2 Samuel are the point. Bathsheba is absent. Absalom's rebellion is absent. What is expanded is David's preparation for the temple — the materials, the musicians, the priestly rotas.",
      "This is not a cover-up so much as a different question. 2 Samuel asks what David was like; Chronicles asks what the worship of the restored community is founded on, and answers by tracing it back to him.",
    ],
    startHere:
      "Chapter 16, the psalm sung when the ark arrives, which is the theological centre of the book. The genealogies can be skimmed without loss on a first reading.",
    themes: ["genealogy", "continuity", "worship", "David and the temple"],
    keyChapters: [
      {
        chapter: 16,
        why: "The psalm sung when the ark arrives, which is the book's theological centre.",
      },
      {
        chapter: 17,
        why: "The promise to David, retold for a community that needed to know it still applied.",
      },
      {
        chapter: 29,
        why: "David's prayer over the temple materials, and the line that everything given came from God first.",
      },
    ],
    known: {
      text: "Oh give thanks to Yahweh, for he is good, for his loving kindness endures forever.",
      ref: "1 Chronicles 16:34",
    },
    misread:
      "That Chronicles is a duplicate of Samuel and Kings. It is a deliberate retelling for a different audience with a different question, and the omissions are as intentional as the additions.",
    faq: [
      {
        q: "Why does Chronicles repeat Samuel and Kings?",
        a: "It is not a repeat but a retelling for the post-exilic community. Chronicles asks what the restored worship rests on, which is why it expands the temple preparations and omits David's private failures.",
      },
      {
        q: "Why does 1 Chronicles start with genealogies?",
        a: "Because the audience had just lost its continuity in exile. A list establishing descent is the book's opening argument that they are still the same people.",
      },
    ],
  },
  {
    slug: "2-chronicles",
    name: "2 Chronicles",
    testament: "old",
    genre: "history",
    order: 14,
    chapters: 36,
    author: "Anonymous; traditionally associated with Ezra",
    written: "Likely fourth century BC",
    audience: "The restored community, being shown what its temple is for",
    argument:
      "Solomon to the exile again, told through the temple, and ending not with destruction but with a decree permitting the return.",
    summary: [
      "The book covers the same ground as Kings and keeps the camera on Judah, ignoring the northern kingdom almost entirely. Its interest is the temple: built, neglected, cleansed, neglected again, and finally destroyed.",
      "The reforming kings get far more space here than in Kings. Hezekiah's passover and Josiah's reforms are told at length, and the pattern the book keeps returning to is repentance producing reprieve — most famously in the promise given at the temple's dedication.",
      "And the ending differs. Kings finishes in the wreckage; Chronicles finishes with Cyrus issuing the decree that lets the exiles go home, which for its first readers was not history but their own recent past.",
    ],
    startHere:
      "Chapter 7 for the dedication promise, then chapter 34 for Josiah's reform. Those two carry the book's whole argument.",
    themes: ["temple", "reform", "repentance", "return"],
    keyChapters: [
      {
        chapter: 7,
        why: "The dedication promise, including the verse most often lifted out of it.",
      },
      {
        chapter: 20,
        why: "Jehoshaphat's prayer under invasion — we do not know what to do, but our eyes are on you.",
      },
      {
        chapter: 34,
        why: "Josiah's reform, the last serious attempt to change the outcome.",
      },
    ],
    known: {
      text: "if my people who are called by my name will humble themselves, pray, seek my face, and turn from their wicked ways, then I will hear from heaven, will forgive their sin, and will heal their land.",
      ref: "2 Chronicles 7:14",
    },
    misread:
      "That 7:14 is a promise to any nation. It is spoken to Solomon about Israel at the dedication of a specific building, and applying it wholesale to a modern country is the most common misuse of a verse in English-speaking Christianity.",
    faq: [
      {
        q: "What is 2 Chronicles 7:14 actually about?",
        a: "It is part of God's answer to Solomon at the temple dedication, addressed to Israel under the covenant and tied to that building. It is regularly quoted about modern nations, which is not what the passage is doing.",
      },
      {
        q: "How does 2 Chronicles end?",
        a: "With the decree of Cyrus permitting the exiles to return — a hopeful ending that Kings, telling the same history, does not have.",
      },
    ],
  },
  {
    slug: "ezra",
    name: "Ezra",
    testament: "old",
    genre: "history",
    order: 15,
    chapters: 10,
    author: "Anonymous; incorporates first-person memoirs attributed to Ezra",
    written: "Likely fourth century BC",
    audience: "A small returned community rebuilding under Persian rule",
    argument:
      "The rebuilding of the temple by people who remembered the old one, and who wept when the foundation was laid because it was so much smaller.",
    summary: [
      "Ezra covers the return from Babylon in stages and the rebuilding of the temple against local opposition and administrative delay. It is a book about small-scale, discouraging, bureaucratic work, which makes it unusual company in the canon.",
      "The scene that defines it is in chapter 3: as the foundation is laid, the young shout for joy and the old men who had seen the first temple weep aloud, and the text says the noise could not be distinguished. That is an unusually precise description of what restoration actually feels like.",
      "The final chapters concern intermarriage and are genuinely difficult, ending with mass separations that the book presents as faithfulness and that most modern readers find troubling. It does not soften them.",
    ],
    startHere:
      "Chapter 3, for the foundation scene, which is the emotional centre of the whole return.",
    themes: ["return", "rebuilding", "opposition", "identity"],
    keyChapters: [
      {
        chapter: 3,
        why: "The foundation laid, the young shouting and the old men weeping, indistinguishably.",
      },
      {
        chapter: 4,
        why: "Opposition by letter and legal delay, which is how most rebuilding actually gets stopped.",
      },
      {
        chapter: 9,
        why: "Ezra's prayer of confession, and the beginning of the book's most difficult section.",
      },
    ],
    known: {
      text: "For Ezra had set his heart to seek Yahweh's law, and to do it, and to teach statutes and ordinances in Israel.",
      ref: "Ezra 7:10",
    },
    misread:
      "That the return was a triumph. The book is candid that it was small, contested and disappointing to everybody who remembered what had been there before.",
    faq: [
      {
        q: "What is the book of Ezra about?",
        a: "The return from Babylonian exile and the rebuilding of the temple, under Persian authorisation and against sustained local opposition.",
      },
      {
        q: "Why did the old men weep in Ezra 3?",
        a: "Because they had seen the first temple and could see how much smaller this one was. The text notes that the weeping and the shouting could not be told apart.",
      },
    ],
  },
  {
    slug: "nehemiah",
    name: "Nehemiah",
    testament: "old",
    genre: "history",
    order: 16,
    chapters: 13,
    author: "Anonymous; built around Nehemiah's first-person memoir",
    written: "Likely fourth century BC",
    audience: "The same community, now rebuilding its walls",
    argument:
      "A civil servant takes leave from a Persian court to rebuild a wall in fifty-two days, and the book is as much about opposition management as about construction.",
    summary: [
      "Nehemiah is the most practical book in the Old Testament. A cupbearer to the Persian king hears Jerusalem's walls are down, asks for leave and materials, arrives, inspects the damage at night, organises the families by section and starts.",
      "The opposition is constant and varied — ridicule, threatened attack, legal complaint, an attempt to lure him to a meeting, a hired prophet advising him to hide. He answers each one and keeps building, and the wall goes up in fifty-two days.",
      "The second half is Ezra reading the law aloud to the assembled people, an all-day public reading that produces first weeping and then a feast, with the instruction that the joy of the Lord is their strength.",
    ],
    startHere:
      "Chapter 2, where he inspects the walls at night and then makes his case. It is the best short study of getting something difficult started in the Bible.",
    themes: ["rebuilding", "opposition", "organisation", "public reading"],
    keyChapters: [
      {
        chapter: 2,
        why: "The night inspection of the walls before anybody is told what he intends.",
      },
      {
        chapter: 4,
        why: "Building with one hand and a weapon in the other, under continuous threat.",
      },
      {
        chapter: 8,
        why: "The law read aloud from morning to midday, the people weeping, and the instruction to feast instead.",
      },
    ],
    known: {
      text: "Don't be grieved, for the joy of Yahweh is your strength.",
      ref: "Nehemiah 8:10",
    },
    misread:
      "That the famous line is about mood. It is said to people weeping at hearing the law, and the instruction attached is to eat well, drink something sweet and send food to anyone who has none.",
    faq: [
      {
        q: "How long did it take Nehemiah to rebuild the wall?",
        a: "Fifty-two days, according to chapter 6 — under continuous opposition, with the workers organised family by family along their own sections.",
      },
      {
        q: "What does the joy of the Lord is your strength mean?",
        a: "It is said to a crowd weeping after hearing the law read aloud, and it comes with practical instructions: eat, drink something sweet, and send portions to those who have nothing prepared.",
      },
    ],
    topics: ["bible-verses-about-strength"],
  },
  {
    slug: "esther",
    name: "Esther",
    testament: "old",
    genre: "history",
    order: 17,
    chapters: 10,
    author: "Anonymous",
    written: "Likely fourth to third century BC",
    audience: "Jews living as a minority in a foreign empire",
    argument:
      "The only book in the Bible that never mentions God, about a genocide averted by a woman who was in the right place and decided to use it.",
    summary: [
      "Esther is set in the Persian court and reads like a court drama: a deposed queen, a beauty contest, a Jewish girl who conceals her identity, a courtier's grudge that escalates into an edict for the extermination of an entire people.",
      "God is not named once. Neither is prayer, the law, the temple or Jerusalem. That absence is almost certainly deliberate, and it is what makes the book useful to readers who have never experienced anything that felt like direct intervention: everything that saves the Jews in this story arrives through coincidence, timing and someone's nerve.",
      "The turning point is Mordecai's argument to Esther — that deliverance will come from somewhere, that she and her family will not survive if she stays silent, and that she may have arrived at her position for exactly this.",
    ],
    startHere:
      "Chapter 4, where Mordecai puts the choice to her. Everything before it is setup and everything after is consequence.",
    themes: ["exile", "courage", "reversal", "hidden providence"],
    keyChapters: [
      {
        chapter: 3,
        why: "Haman's edict, and the casting of lots that gives the festival its name.",
      },
      {
        chapter: 4,
        why: "For such a time as this — the argument that forces the decision.",
      },
      {
        chapter: 9,
        why: "The reversal, and the establishment of Purim, which is still kept.",
      },
    ],
    known: {
      text: "Who knows if you haven't come to the kingdom for such a time as this?",
      ref: "Esther 4:14",
    },
    misread:
      "That it is a story about a beauty queen. Esther is a hostage in a harem before she is a heroine, the book is frank about that, and her courage is exercised inside a situation nobody would choose.",
    faq: [
      {
        q: "Why is God not mentioned in Esther?",
        a: "Nobody knows for certain, and it is almost certainly deliberate. The effect is a book in which everything that saves the people arrives through timing, coincidence and human nerve.",
      },
      {
        q: "What festival comes from the book of Esther?",
        a: "Purim, which commemorates the reversal of the edict and is still kept annually. The book closes by establishing it.",
      },
    ],
  },
];
